// src/components/EntriesList/EditEntryModal.js
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import { format } from 'date-fns';
import Select from 'react-select';
import { parseDate } from './utils/dateUtils';
import { styles } from './styles/entryStyles';

registerLocale('de', de);

const aenderungsGruende = ['-', 'falsche Eingabe', 'Streit Korrektur', 'weil...'];

/** Custom Input für DatePicker */
const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    type="text"
    onClick={onClick}
    value={value}
    placeholder={placeholder}
    ref={ref}
    style={styles.datePickerInput}
    readOnly
  />
));

/**
 * Komponente zum Bearbeiten eines Eintrags.
 * Die Modal-Komponente ist in drei Sektionen (Allgemeine Infos, Zeiten & Finanzen und Änderungsdetails) unterteilt.
 */
function EditEntryModal({
  entry,
  onCancel,
  onUpdate,
  onShowHistory,
  stundensatzJahr,
  stundensatzFachgebiet,
  customerOptions,
}) {
  const initialFormState = {
    date: entry.date,
    type: entry.type,
    subject: entry.subject || '',
    address: entry.address || '',
    startTime: entry.startTime || '',
    endTime: entry.endTime || '',
    hours: entry.hours || '',
    hotelspeesen: entry.hotelspeesen === true || entry.hotelspeesen === 1,
    verpflegungsMehraufwand: entry.verpflegungsMehraufwand === true || entry.verpflegungsMehraufwand === 1,
    hourlyWage: entry.hourlyWage || stundensatzJahr,
    customerId: entry.customerId || null,
    fachgebiet: entry.fachgebiet || 0,
    homeoffice: entry.homeoffice === true || entry.homeoffice === 1,
    abgerechnet: entry.abgerechnet === true || entry.abgerechnet === 1,
    aenderungsGrund: entry.aenderungsGrund || '',
    aenderungsText: entry.aenderungsText || '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [date, setDate] = useState(parseDate(entry.date));
  const [activeTab, setActiveTab] = useState('allgemein');
  const [selectedCustomer, setSelectedCustomer] = useState(
    customerOptions.find(opt => opt.value === entry.customerId) || null
  );

  // Handler für Eingabefelder
  // Handler für Eingabefelder
const handleFormChange = (e) => {
  const { name, type, value, checked } = e.target;
  
  if (type === 'checkbox') {
    if (name === 'Workflow') {
      setFormData(prev => ({
        ...prev,
        fachgebiet: checked ? 1 : 0,
        hourlyWage: checked ? stundensatzFachgebiet : stundensatzJahr,
      }));
    } else if (name === 'Finanzbuchhaltung') {
      setFormData(prev => ({
        ...prev,
        fachgebiet: checked ? 2 : 0,
        hourlyWage: checked ? stundensatzFachgebiet : stundensatzJahr,
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: checked }));
    }
  } else {
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      
      // Automatische Berechnung der Stunden, wenn Start- oder Endzeit geändert wurde
      if (name === 'startTime' || name === 'endTime') {
        if (updatedData.startTime && updatedData.endTime) {
          // Startzeit in Minuten umrechnen
          const [startHours, startMinutes] = updatedData.startTime.split(':').map(Number);
          const startTimeInMinutes = startHours * 60 + startMinutes;
          
          // Endzeit in Minuten umrechnen
          const [endHours, endMinutes] = updatedData.endTime.split(':').map(Number);
          const endTimeInMinutes = endHours * 60 + endMinutes;
          
          // Falls Endzeit vor Startzeit liegt, wird angenommen, dass die Endzeit am nächsten Tag ist
          let differenceInMinutes = endTimeInMinutes - startTimeInMinutes;
          if (differenceInMinutes < 0) {
            differenceInMinutes += 24 * 60; // 24 Stunden in Minuten
          }
          
          // Umrechnung in Stunden mit 2 Dezimalstellen
          const hours = Math.round((differenceInMinutes / 60) * 4) / 4; // Runden auf 0.25 Schritte
          updatedData.hours = hours.toString();
        }
      }
      
      return updatedData;
    });
  }
};

  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData(prev => ({ ...prev, date: format(selectedDate, 'yyyy-MM-dd') }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedEntry = { ...formData, hours: parseFloat(formData.hours) || 0 };
    onUpdate(updatedEntry);
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContainer}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2>Eintrag bearbeiten</h2>
          <button type="button" onClick={onCancel} style={styles.modalCloseBtn}>
            ✖
          </button>
        </div>

        {/* Tab-Navigation */}
        <div style={styles.tabHeader}>
          <button
            type="button"
            style={activeTab === 'allgemein' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => setActiveTab('allgemein')}
          >
            Allgemein
          </button>
          <button
            type="button"
            style={activeTab === 'zeiten' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => setActiveTab('zeiten')}
          >
            Zeiten &amp; Finanzen
          </button>
          <button
            type="button"
            style={activeTab === 'aenderungen' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => setActiveTab('aenderungen')}
          >
            Änderungen
          </button>
        </div>

        {/* Flex-Container: Inhaltsbereich (scrollbar) + Footer */}
        <div style={styles.contentWrapper}>
          <div style={styles.modalContent}>
            {activeTab === 'allgemein' && (
              <>
                <div style={styles.formGroup}>
                  <label>Datum:</label>
                  <DatePicker
                    selected={date}
                    onChange={handleDateChange}
                    dateFormat="dd.MM.yyyy"
                    locale="de"
                    placeholderText="TT.MM.JJJJ"
                    customInput={<CustomDateInput />}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Art des Tages:</label>
                  <select name="type" value={formData.type} onChange={handleFormChange} style={styles.input}>
                    <option value="normal">Normal</option>
                    <option value="halb-vormittag">Halber Tag (Vormittag)</option>
                    <option value="halb-nachmittag">Halber Tag (Nachmittag)</option>
                    <option value="urlaub">Urlaub</option>
                    <option value="feiertag">Feiertag</option>
                    <option value="krank">Krank</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Betreff / Auftrag:</label>
                  <textarea
                    name="subject"
                    value={formData.subject}
                    onChange={handleFormChange}
                    style={styles.input}
                    placeholder="z. B. Projekt XY"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Adresse:</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    style={styles.input}
                    placeholder="z. B. Musterstraße 1"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Kunde:</label>
                  <Select
                    value={selectedCustomer}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      setFormData(prev => ({ ...prev, customerId: option ? option.value : null }));
                    }}
                    options={customerOptions}
                    isClearable
                    placeholder="Kunde auswählen"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '4px',
                        borderColor: '#ccc',
                        fontSize: '1rem',
                        minHeight: '38px',
                      }),
                    }}
                  />
                </div>
                <div style={styles.formGroupRow}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="Finanzbuchhaltung"
                      checked={formData.fachgebiet === 2}
                      onChange={handleFormChange}
                    />
                    Finanzbuchhaltung
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="Workflow"
                      checked={formData.fachgebiet === 1}
                      onChange={handleFormChange}
                    />
                    Workflow
                  </label>
                </div>
              </>
            )}

            {activeTab === 'zeiten' && (
              <>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroup}>
                    <label>Startzeit:</label>
                    <input
                      type="time"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleFormChange}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label>Endzeit:</label>
                    <input
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleFormChange}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroup}>
                    <label>Stunden:</label>
                    <input
                      type="number"
                      name="hours"
                      step="0.01"
                      value={formData.hours}
                      onChange={handleFormChange}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label>Stundensatz:</label>
                    <input
                      type="number"
                      name="hourlyWage"
                      step="0.01"
                      value={formData.hourlyWage}
                      onChange={handleFormChange}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.formGroupRow}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="hotelspeesen"
                      checked={formData.hotelspeesen}
                      onChange={handleFormChange}
                    />
                    Hotelspeesen
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="verpflegungsMehraufwand"
                      checked={formData.verpflegungsMehraufwand}
                      onChange={handleFormChange}
                    />
                    Verpflegung
                  </label>
                </div>
                <div style={styles.formGroupRow}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="homeoffice"
                      checked={formData.homeoffice}
                      onChange={handleFormChange}
                    />
                    Homeoffice
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="abgerechnet"
                      checked={formData.abgerechnet}
                      onChange={handleFormChange}
                    />
                    Abgerechnet
                  </label>
                </div>
              </>
            )}

            {activeTab === 'aenderungen' && (
              <>
                <div style={styles.formGroup}>
                  <label>Änderungsgrund:</label>
                  <select
                    name="aenderungsGrund"
                    value={formData.aenderungsGrund}
                    onChange={handleFormChange}
                    style={styles.input}
                  >
                    {aenderungsGruende.map((grund) => (
                      <option key={grund} value={grund}>
                        {grund}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Zusätzlicher Grund:</label>
                  <input
                    type="text"
                    name="aenderungsText"
                    value={formData.aenderungsText}
                    onChange={handleFormChange}
                    style={styles.input}
                    placeholder="Optionaler Zusatz"
                  />
                </div>
              </>
            )}
          </div>
          {/* Footer (fixiert am unteren Rand des Containers) */}
          <div style={styles.modalFooter}>
            <button type="button" onClick={onShowHistory} style={styles.historyBtn}>
              Historie anzeigen
            </button>
            <button type="button" onClick={onCancel} style={styles.cancelBtn}>
              Abbrechen
            </button>
            <button type="button" onClick={handleSubmit} style={styles.updateBtn}>
              Aktualisieren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditEntryModal;